import type {
  CustomerCreditRuntimeItem,
  EmployeeDirectoryRuntimeItem,
  LocalProjectionDomain,
  LocalProjectionPayload,
  MemberRuntimeItem,
  RuntimeCursor,
  StockRuntimeItem,
} from "../gateway/local-projection-client";
import type { PosSaleMember } from "../gateway/sale-context-client";
import { posRuntimeDb } from "./pos-db";
import type { TrustedTerminalIdentity } from "./terminal-state";

const ZERO_UUID = "00000000-0000-0000-0000-000000000000";

function clean(value: string | null | undefined) {
  return (value ?? "").trim().toLocaleLowerCase("th").replace(/\s+/g, " ");
}

export function normalizeIdentifier(value: string | null | undefined) {
  return clean(value).replace(/[\s()-]/g, "");
}

function tokens(...values: Array<string | null | undefined>) {
  const result = new Set<string>();
  for (const raw of values) {
    const value = clean(raw);
    if (!value) continue;
    result.add(value);
    for (const part of value.split(/[^\p{L}\p{N}@._+-]+/u).filter(Boolean)) result.add(part);
    const compact = normalizeIdentifier(value);
    if (compact) result.add(compact);
  }
  return [...result];
}

export function indexedScope(identity: TrustedTerminalIdentity, domain: string) {
  return `org:${identity.organization_id}:branch:${identity.branch_id}:node:${identity.node_id}:${domain}`;
}

function cursorKey(identity: TrustedTerminalIdentity, domain: LocalProjectionDomain) {
  return `local-index:cursor:${indexedScope(identity, domain.toLocaleLowerCase())}`;
}

export async function readLocalProjectionCursor(identity: TrustedTerminalIdentity, domain: LocalProjectionDomain): Promise<RuntimeCursor | null> {
  const row = await posRuntimeDb.runtimeMeta.get(cursorKey(identity, domain));
  if (!row?.value || typeof row.value !== "object") return null;
  const candidate = row.value as Partial<RuntimeCursor>;
  return typeof candidate.changed_at === "string" && typeof candidate.entity_id === "string"
    ? { changed_at: candidate.changed_at, entity_id: candidate.entity_id }
    : null;
}

async function writeCursor(
  identity: TrustedTerminalIdentity,
  domain: LocalProjectionDomain,
  cursor: RuntimeCursor | null,
  fallbackServerAt: string,
) {
  const effective = cursor?.changed_at && cursor.entity_id
    ? cursor
    : { changed_at: fallbackServerAt, entity_id: ZERO_UUID };
  const now = new Date().toISOString();
  await posRuntimeDb.runtimeMeta.put({
    key: cursorKey(identity, domain),
    scopeKey: indexedScope(identity, domain.toLocaleLowerCase()),
    value: effective,
    updatedAt: now,
  });
}

function memberKey(scopeKey: string, customerId: string) {
  return `${scopeKey}:customer:${customerId}`;
}

function memberIdentifierKey(scopeKey: string, customerId: string, type: string, normalized: string) {
  return `${scopeKey}:customer:${customerId}:identifier:${type}:${normalized}`;
}

export async function applyMemberRuntime(identity: TrustedTerminalIdentity, payload: LocalProjectionPayload<"MEMBER">) {
  const scopeKey = indexedScope(identity, "member");
  await posRuntimeDb.transaction("rw", posRuntimeDb.members, posRuntimeDb.memberIdentifiers, posRuntimeDb.runtimeMeta, async () => {
    if (payload.mode === "FULL") {
      await posRuntimeDb.members.where("scopeKey").equals(scopeKey).delete();
      await posRuntimeDb.memberIdentifiers.where("scopeKey").equals(scopeKey).delete();
    }
    for (const item of payload.items) {
      const key = memberKey(scopeKey, item.customer_id);
      await posRuntimeDb.memberIdentifiers.where("customerId").equals(item.customer_id).and((row) => row.scopeKey === scopeKey).delete();
      if (!item.active) {
        await posRuntimeDb.members.delete(key);
        continue;
      }
      const identifierTokens = (item.identifiers ?? []).flatMap((entry) => [entry.normalized_value, entry.display_value]);
      await posRuntimeDb.members.put({
        key,
        scopeKey,
        customerId: item.customer_id,
        membershipId: item.membership_id,
        customerCode: item.customer_code,
        customerName: item.customer_name,
        normalizedName: clean(item.customer_name),
        phone: item.phone,
        email: item.email,
        membershipStatus: item.membership_status,
        joinedAt: item.joined_at,
        searchTokens: tokens(item.customer_name, item.customer_code, item.phone, item.email, ...identifierTokens),
        changedAt: item.changed_at,
        payload: item,
      });
      for (const identifier of item.identifiers ?? []) {
        const normalized = normalizeIdentifier(identifier.normalized_value || identifier.display_value);
        if (!normalized) continue;
        await posRuntimeDb.memberIdentifiers.put({
          key: memberIdentifierKey(scopeKey, item.customer_id, identifier.identifier_type, normalized),
          scopeKey,
          customerId: item.customer_id,
          membershipId: item.membership_id,
          identifierType: identifier.identifier_type,
          normalizedValue: normalized,
          displayValue: identifier.display_value,
          isPrimary: identifier.is_primary,
          changedAt: item.changed_at,
        });
      }
      const normalizedPhone = normalizeIdentifier(item.phone);
      if (normalizedPhone && !(item.identifiers ?? []).some((entry) => normalizeIdentifier(entry.normalized_value || entry.display_value) === normalizedPhone)) {
        await posRuntimeDb.memberIdentifiers.put({
          key: memberIdentifierKey(scopeKey, item.customer_id, "PHONE", normalizedPhone),
          scopeKey,
          customerId: item.customer_id,
          membershipId: item.membership_id,
          identifierType: "PHONE",
          normalizedValue: normalizedPhone,
          displayValue: item.phone,
          isPrimary: true,
          changedAt: item.changed_at,
        });
      }
    }
    await writeCursor(identity, "MEMBER", payload.next_cursor, payload.server_at);
  });
}

function memberFromRecord(payload: unknown): PosSaleMember | null {
  if (!payload || typeof payload !== "object") return null;
  const item = payload as MemberRuntimeItem;
  if (!item.active) return null;
  return {
    customer_id: item.customer_id,
    membership_id: item.membership_id,
    customer_code: item.customer_code,
    customer_name: item.customer_name,
    phone: item.phone,
    email: item.email,
    membership_status: item.membership_status,
    joined_at: item.joined_at,
    identifiers: (item.identifiers ?? []).map((entry) => ({
      identifier_type: entry.identifier_type,
      display_value: entry.display_value,
      is_primary: entry.is_primary,
    })),
  };
}

export async function searchLocalMembers(identity: TrustedTerminalIdentity, searchText: string, limit = 20): Promise<PosSaleMember[]> {
  const scopeKey = indexedScope(identity, "member");
  const query = clean(searchText);
  const normalized = normalizeIdentifier(searchText);
  const customerIds: string[] = [];
  if (normalized) {
    const exact = await posRuntimeDb.memberIdentifiers.where("[scopeKey+normalizedValue]").equals([scopeKey, normalized]).toArray();
    customerIds.push(...exact.map((row) => row.customerId));
  }
  if (customerIds.length < limit && query) {
    const prefix = await posRuntimeDb.members.where("searchTokens").startsWithIgnoreCase(query).and((row) => row.scopeKey === scopeKey).limit(limit).toArray();
    customerIds.push(...prefix.map((row) => row.customerId));
  }
  if (!query) {
    const rows = await posRuntimeDb.members.where("scopeKey").equals(scopeKey).limit(limit).toArray();
    customerIds.push(...rows.map((row) => row.customerId));
  }
  const unique = [...new Set(customerIds)].slice(0, limit);
  const rows = await Promise.all(unique.map((customerId) => posRuntimeDb.members.get(memberKey(scopeKey, customerId))));
  return rows.flatMap((row) => {
    const member = row ? memberFromRecord(row.payload) : null;
    return member ? [member] : [];
  });
}

function creditKey(scopeKey: string, customerId: string) {
  return `${scopeKey}:customer:${customerId}`;
}

export async function applyCustomerCreditRuntime(identity: TrustedTerminalIdentity, payload: LocalProjectionPayload<"CUSTOMER_CREDIT">) {
  const scopeKey = indexedScope(identity, "customer-credit");
  await posRuntimeDb.transaction("rw", posRuntimeDb.customerCredits, posRuntimeDb.runtimeMeta, async () => {
    if (payload.mode === "FULL") await posRuntimeDb.customerCredits.where("scopeKey").equals(scopeKey).delete();
    for (const item of payload.items) {
      await posRuntimeDb.customerCredits.put({
        key: creditKey(scopeKey, item.customer_id),
        scopeKey,
        customerId: item.customer_id,
        creditAccountId: item.credit_account_id,
        status: item.status,
        eligible: item.eligible,
        branchEligible: item.branch_eligible,
        creditLimit: item.credit_limit == null ? null : Number(item.credit_limit),
        currencyCode: item.currency_code,
        paymentTermDays: item.payment_term_days,
        versionNo: Number(item.version_no),
        changedAt: item.changed_at,
        payload: item,
      });
    }
    await writeCursor(identity, "CUSTOMER_CREDIT", payload.next_cursor, payload.server_at);
  });
}

export async function readLocalCustomerCredit(identity: TrustedTerminalIdentity, customerId: string): Promise<CustomerCreditRuntimeItem | null> {
  const row = await posRuntimeDb.customerCredits.get(creditKey(indexedScope(identity, "customer-credit"), customerId));
  return row?.payload && typeof row.payload === "object" ? row.payload as CustomerCreditRuntimeItem : null;
}

function stockKey(scopeKey: string, stockItemId: string) {
  return `${scopeKey}:item:${stockItemId}`;
}

function stockIdentifierKey(scopeKey: string, stockItemId: string, type: string, normalizedValue: string) {
  return `${scopeKey}:item:${stockItemId}:identifier:${type}:${normalizedValue}`;
}

export async function applyStockRuntime(identity: TrustedTerminalIdentity, payload: LocalProjectionPayload<"STOCK">) {
  const scopeKey = indexedScope(identity, "stock");
  await posRuntimeDb.transaction("rw", posRuntimeDb.stockRuntime, posRuntimeDb.stockIdentifiers, posRuntimeDb.runtimeMeta, async () => {
    if (payload.mode === "FULL" || payload.mode === "DISABLED") {
      await posRuntimeDb.stockRuntime.where("scopeKey").equals(scopeKey).delete();
      await posRuntimeDb.stockIdentifiers.where("scopeKey").equals(scopeKey).delete();
    }
    if (payload.mode === "DISABLED") {
      await writeCursor(identity, "STOCK", payload.next_cursor, payload.server_at);
      return;
    }
    for (const item of payload.items) {
      await posRuntimeDb.stockIdentifiers.where("stockItemId").equals(item.stock_item_id).and((row) => row.scopeKey === scopeKey).delete();
      if (!item.active) {
        await posRuntimeDb.stockRuntime.delete(stockKey(scopeKey, item.stock_item_id));
        continue;
      }
      await posRuntimeDb.stockRuntime.put({
        key: stockKey(scopeKey, item.stock_item_id),
        scopeKey,
        stockItemId: item.stock_item_id,
        itemCode: item.item_code,
        skuCode: item.sku_code,
        normalizedName: clean(item.canonical_name || item.normalized_name),
        categoryCode: item.category_code,
        active: true,
        quantityOnHand: item.quantity_on_hand == null ? null : Number(item.quantity_on_hand),
        stateVersion: item.state_version == null ? null : Number(item.state_version),
        searchTokens: tokens(item.canonical_name, item.normalized_name, item.item_code, item.sku_code, item.category_code, item.category_name, ...(item.barcodes ?? [])),
        changedAt: item.changed_at,
        payload: item,
      });
      const identifiers: Array<[string, Array<string | null>]> = [
        ["ITEM_CODE", [item.item_code]],
        ["SKU", [item.sku_code]],
        ["BARCODE", item.barcodes ?? []],
      ];
      for (const [type, values] of identifiers) {
        for (const raw of values) {
          const normalizedValue = normalizeIdentifier(raw);
          if (!normalizedValue) continue;
          await posRuntimeDb.stockIdentifiers.put({
            key: stockIdentifierKey(scopeKey, item.stock_item_id, type, normalizedValue),
            scopeKey,
            stockItemId: item.stock_item_id,
            identifierType: type,
            normalizedValue,
            changedAt: item.changed_at,
          });
        }
      }
    }
    await writeCursor(identity, "STOCK", payload.next_cursor, payload.server_at);
  });
}

export async function searchLocalStock(identity: TrustedTerminalIdentity, searchText: string, limit = 30): Promise<StockRuntimeItem[]> {
  const scopeKey = indexedScope(identity, "stock");
  const normalized = normalizeIdentifier(searchText);
  const query = clean(searchText);
  const ids: string[] = [];
  if (normalized) {
    const exact = await posRuntimeDb.stockIdentifiers.where("[scopeKey+normalizedValue]").equals([scopeKey, normalized]).toArray();
    ids.push(...exact.map((row) => row.stockItemId));
  }
  if (ids.length < limit && query) {
    const prefix = await posRuntimeDb.stockRuntime.where("searchTokens").startsWithIgnoreCase(query).and((row) => row.scopeKey === scopeKey && row.active).limit(limit).toArray();
    ids.push(...prefix.map((row) => row.stockItemId));
  }
  const rows = await Promise.all([...new Set(ids)].slice(0, limit).map((id) => posRuntimeDb.stockRuntime.get(stockKey(scopeKey, id))));
  return rows.flatMap((row) => row?.payload && typeof row.payload === "object" ? [row.payload as StockRuntimeItem] : []);
}

function employeeKey(scopeKey: string, employeeId: string) {
  return `${scopeKey}:employee:${employeeId}`;
}

export async function applyEmployeeDirectoryRuntime(identity: TrustedTerminalIdentity, payload: LocalProjectionPayload<"EMPLOYEE_DIRECTORY">) {
  const scopeKey = indexedScope(identity, "employee-directory");
  await posRuntimeDb.transaction("rw", posRuntimeDb.employeeDirectory, posRuntimeDb.runtimeMeta, async () => {
    if (payload.mode === "FULL") await posRuntimeDb.employeeDirectory.where("scopeKey").equals(scopeKey).delete();
    for (const item of payload.items) {
      const key = employeeKey(scopeKey, item.employee_id);
      if (!item.eligible) {
        await posRuntimeDb.employeeDirectory.delete(key);
        continue;
      }
      await posRuntimeDb.employeeDirectory.put({
        key,
        scopeKey,
        employeeId: item.employee_id,
        employeeCode: item.employee_code,
        employeeName: item.employee_name,
        normalizedName: clean(item.employee_name),
        employmentStatus: item.employment_status,
        eligible: true,
        searchTokens: tokens(item.employee_name, item.employee_code),
        changedAt: item.changed_at,
      });
    }
    await writeCursor(identity, "EMPLOYEE_DIRECTORY", payload.next_cursor, payload.server_at);
  });
}

export async function readEligibleEmployeeDirectory(identity: TrustedTerminalIdentity): Promise<EmployeeDirectoryRuntimeItem[]> {
  const scopeKey = indexedScope(identity, "employee-directory");
  const rows = await posRuntimeDb.employeeDirectory.where("scopeKey").equals(scopeKey).and((row) => row.eligible).sortBy("employeeCode");
  return rows.map((row) => ({
    employee_id: row.employeeId,
    employee_code: row.employeeCode,
    employee_name: row.employeeName,
    employment_status: row.employmentStatus,
    eligible: row.eligible,
    changed_at: row.changedAt,
  }));
}

export async function purgeIndexedRuntimeOutsideScope(identity: TrustedTerminalIdentity) {
  const keepPrefix = `org:${identity.organization_id}:branch:${identity.branch_id}:node:${identity.node_id}:`;
  await Promise.all([
    posRuntimeDb.members.filter((row) => !row.scopeKey.startsWith(keepPrefix)).delete(),
    posRuntimeDb.memberIdentifiers.filter((row) => !row.scopeKey.startsWith(keepPrefix)).delete(),
    posRuntimeDb.customerCredits.filter((row) => !row.scopeKey.startsWith(keepPrefix)).delete(),
    posRuntimeDb.loyaltyRuntime.filter((row) => !row.scopeKey.startsWith(keepPrefix)).delete(),
    posRuntimeDb.stockRuntime.filter((row) => !row.scopeKey.startsWith(keepPrefix)).delete(),
    posRuntimeDb.stockIdentifiers.filter((row) => !row.scopeKey.startsWith(keepPrefix)).delete(),
    posRuntimeDb.employeeDirectory.filter((row) => !row.scopeKey.startsWith(keepPrefix)).delete(),
    posRuntimeDb.receivingQueue.filter((row) => !row.scopeKey.startsWith(keepPrefix)).delete(),
    posRuntimeDb.salesHistory.filter((row) => !row.scopeKey.startsWith(keepPrefix)).delete(),
    posRuntimeDb.salesOrderDetails.filter((row) => !row.scopeKey.startsWith(keepPrefix)).delete(),
    posRuntimeDb.shiftRuntime.filter((row) => !row.scopeKey.startsWith(keepPrefix)).delete(),
    posRuntimeDb.catalogLookup.filter((row) => !row.scopeKey.startsWith(keepPrefix)).delete(),
    posRuntimeDb.runtimeMeta.filter((row) => row.key.startsWith("local-index:cursor:") && !row.scopeKey.startsWith(keepPrefix)).delete(),
  ]);
}
